function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var n = Object.getOwnPropertySymbols(e);
    for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}
/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { getSecureRelForTarget } from '../../../services';
import { keysOf } from '../../common';
import { OuiIcon } from '../../icon';
import { validateHref } from '../../../services/security/href_validator';
var displayToClassNameMap = {
  base: null,
  empty: 'ouiButtonIcon--empty',
  fill: 'ouiButtonIcon--fill'
};
export var DISPLAYS = keysOf(displayToClassNameMap);
var colorToClassNameMap = {
  accent: 'ouiButtonIcon--accent',
  danger: 'ouiButtonIcon--danger',
  ghost: 'ouiButtonIcon--ghost',
  primary: 'ouiButtonIcon--primary',
  subdued: 'ouiButtonIcon--subdued',
  success: 'ouiButtonIcon--success',
  text: 'ouiButtonIcon--text',
  warning: 'ouiButtonIcon--warning'
};
export var COLORS = keysOf(colorToClassNameMap);
var sizeToClassNameMap = {
  xs: 'ouiButtonIcon--xSmall',
  s: 'ouiButtonIcon--small',
  m: 'ouiButtonIcon--medium'
};
export var SIZES = keysOf(sizeToClassNameMap);
export var OuiButtonIcon = function OuiButtonIcon(_ref) {
  var className = _ref.className,
    iconType = _ref.iconType,
    _ref$iconSize = _ref.iconSize,
    iconSize = _ref$iconSize === void 0 ? 'm' : _ref$iconSize,
    _ref$color = _ref.color,
    color = _ref$color === void 0 ? 'primary' : _ref$color,
    _isDisabled = _ref.isDisabled,
    href = _ref.href,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'button' : _ref$type,
    _ref$display = _ref.display,
    display = _ref$display === void 0 ? 'empty' : _ref$display,
    target = _ref.target,
    rel = _ref.rel,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'xs' : _ref$size,
    buttonRef = _ref.buttonRef,
    isSelected = _ref.isSelected,
    rest = _objectWithoutProperties(_ref, ["className", "iconType", "iconSize", "color", "isDisabled", "href", "type", "display", "target", "rel", "size", "buttonRef", "isSelected"]);
  var isHrefValid = !href || validateHref(href);
  var isDisabled = _isDisabled || !isHrefValid;
  var ariaHidden = rest['aria-hidden'];
  var isAriaHidden = ariaHidden === 'true' || ariaHidden === true;
  if (!rest['aria-label'] && !rest['aria-labelledby'] && !isAriaHidden) {
    console.warn("OuiButtonIcon requires aria-label or aria-labelledby to be specified because icon-only\n      buttons are screen-reader-inaccessible without them.");
  }
  var classes = classNames('ouiButtonIcon', {
    'ouiButtonIcon-isDisabled': isDisabled
  }, colorToClassNameMap[color], display && displayToClassNameMap[display], size && sizeToClassNameMap[size], className);

  // Add an icon to the button if one exists.
  var buttonIcon;
  if (iconType) {
    buttonIcon = /*#__PURE__*/React.createElement(OuiIcon, {
      className: "ouiButtonIcon__icon",
      type: iconType,
      size: iconSize,
      "aria-hidden": "true",
      color: "inherit" // forces the icon to inherit its parent color
    });
  }

  // <a> elements don't respect the `disabled` attribute. So if we're disabled, we'll just pretend
  // this is a button and piggyback off its disabled styles.
  if (href && !isDisabled) {
    var secureRel = getSecureRelForTarget({
      href: href,
      target: target,
      rel: rel
    });
    return /*#__PURE__*/React.createElement("a", _extends({
      tabIndex: isAriaHidden ? -1 : undefined,
      className: classes,
      href: href,
      target: target,
      rel: secureRel,
      ref: buttonRef
    }, rest), buttonIcon);
  }
  var buttonType;
  return /*#__PURE__*/React.createElement("button", _extends({
    tabIndex: isAriaHidden ? -1 : undefined,
    disabled: isDisabled,
    className: classes,
    "aria-pressed": isSelected,
    type: type,
    ref: buttonRef
  }, rest), buttonIcon);
};

// @internal
OuiButtonIcon.propTypes = {
  type: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(["submit", "reset", "button"])]),
  href: PropTypes.string,
  onClick: PropTypes.func,
  iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addBookmark", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "anomalyDetection", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "bookOpen", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "chatLeft", "chatRight", "check", "checkInCircleEmpty", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "color", "compass", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "crossInCircleEmpty", "crossInCircleFilled", "currency", "cut", "dashboard", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentation", "documentEdit", "documents", "dot", "download", "dockedBottom", "dockedDetached", "dockedLeft", "dockedRight", "dockedTakeover", "dockedTop", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "functionAdd", "gear", "generate", "generateContent", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "history", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "integrationGeneral", "integrationObservability", "integrationSearch", "integrationSecurity", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lineChart", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOpenSearch", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "polygon", "popout", "power", "pulse", "push", "questionInCircle", "quote", "radar", "radius", "recent", "recentlyViewedApp", "redeploy", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "rocket", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sparkleFilled", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "thumbsDown", "thumbsUp", "timeline", "timelionApp", "timeslider", "training", "trash", "upgradeAssistantApp", "uptimeApp", "undeploy", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visBuilder", "visBuilderSavedObject", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visQueryDQL", "visQueryPPL", "visQueryPromQL", "visQuerySQL", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram", "wsAnalytics", "wsEssentials", "wsObservability", "wsSearch", "wsSecurityAnalytics", "wsSelector", "navAdministration", "navAiFlow", "navAlerting", "navAnomalyDetection", "navDashboards", "navData", "navDetectionRules", "navDevtools", "navDiscover", "navExperiments", "navGetStarted", "navInfo", "navInfra", "navIntegrations", "navJudgements", "navManage", "navMaps", "navModels", "navNotebooks", "navNotifications", "navOverview", "navQuerySets", "navReports", "navSecurityCases", "navSecurityFindings", "navServiceMap", "navServices", "navSlos", "navThreatIntel", "navTicketing", "navUi", "navSearchConfigurationsln"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired,
  /**
     * Any of the named color palette options.
     * **`subdued` set to be DEPRECATED, use `text` instead**
     */
  /**
     * Any of the named color palette options.
     * **`subdued` set to be DEPRECATED, use `text` instead**
     */
  color: PropTypes.oneOf(["accent", "danger", "ghost", "primary", "subdued", "success", "text", "warning"]),
  "aria-label": PropTypes.string,
  "aria-labelledby": PropTypes.string,
  isDisabled: PropTypes.bool,
  /**
     * Overall size of button.
     * Matches the sizes of other OuiButtons
     */
  /**
     * Overall size of button.
     * Matches the sizes of other OuiButtons
     */
  size: PropTypes.oneOf(["xs", "s", "m"]),
  /**
     * Size of the icon only.
     * This will not affect the overall size of the button
     */
  iconSize: PropTypes.oneOf(["original", "s", "m", "l", "xl", "xxl"]),
  /**
     * Applies the boolean state as the `aria-pressed` property to create a toggle button.
     * *Only use when the readable text does not change between states.*
     */
  /**
     * Applies the boolean state as the `aria-pressed` property to create a toggle button.
     * *Only use when the readable text does not change between states.*
     */
  isSelected: PropTypes.bool,
  /**
     * Sets the display style for matching other OuiButton types.
     * `base` is equivelant to a typical OuiButton
     * `fill` is equivelant to a filled OuiButton
     * `empty` (default) is equivelant to an OuiButtonEmpty
     */
  /**
     * Sets the display style for matching other OuiButton types.
     * `base` is equivelant to a typical OuiButton
     * `fill` is equivelant to a filled OuiButton
     * `empty` (default) is equivelant to an OuiButtonEmpty
     */
  display: PropTypes.oneOf(["base", "empty", "fill"]),
  className: PropTypes.string,
  "data-test-subj": PropTypes.string,
  buttonRef: PropTypes.any
}; // @internal
// @internal
export var OuiSmallButtonIcon = props => <OuiButtonIcon {...props} size="s" />;
try {
  OuiButtonIcon.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src/components/button/button_icon/button_icon.tsx',
    description: '',
    displayName: 'OuiButtonIcon',
    methods: [],
    props: {
      href: {
        defaultValue: null,
        description: '',
        name: 'href',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AnchorHTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      type: {
        defaultValue: {
          value: 'button'
        },
        description: '',
        name: 'type',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'ButtonHTMLAttributes'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AnchorHTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      iconType: {
        defaultValue: null,
        description: '',
        name: 'iconType',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: true,
        type: {
          name: 'IconType'
        }
      },
      color: {
        defaultValue: {
          value: 'primary'
        },
        description: 'Any of the named color palette options.\n' + '**`subdued` set to be DEPRECATED, use `text` instead**',
        name: 'color',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"text" | "subdued" | "success" | "accent" | "danger" | "warning" | "ghost" | "primary"',
          value: [{
            value: '"text"'
          }, {
            value: '"subdued"'
          }, {
            value: '"success"'
          }, {
            value: '"accent"'
          }, {
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"ghost"'
          }, {
            value: '"primary"'
          }]
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-labelledby': {
        defaultValue: null,
        description: 'Identifies the element (or elements) that labels the current element.\n' + '@see aria-describedby.',
        name: 'aria-labelledby',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: '',
        name: 'isDisabled',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      size: {
        defaultValue: {
          value: 'xs'
        },
        description: 'Overall size of button.\nMatches the sizes of other OuiButtons',
        name: 'size',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"xs" | "s" | "m"',
          value: [{
            value: '"xs"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      },
      iconSize: {
        defaultValue: {
          value: 'm'
        },
        description: 'Size of the icon only.\n' + 'This will not affect the overall size of the button',
        name: 'iconSize',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l" | "xl" | "xxl" | "original"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }, {
            value: '"xxl"'
          }, {
            value: '"original"'
          }]
        }
      },
      isSelected: {
        defaultValue: null,
        description: 'Applies the boolean state as the `aria-pressed` property to create a toggle button.\n' + '*Only use when the readable text does not change between states.*',
        name: 'isSelected',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      display: {
        defaultValue: {
          value: 'empty'
        },
        description: 'Sets the display style for matching other OuiButton types.\n' + '`base` is equivelant to a typical OuiButton\n' + '`fill` is equivelant to a filled OuiButton\n' + '`empty` (default) is equivelant to an OuiButtonEmpty',
        name: 'display',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"base" | "empty" | "fill"',
          value: [{
            value: '"base"'
          }, {
            value: '"empty"'
          }, {
            value: '"fill"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLAnchorElement> | MouseEventHandler<HTMLButtonElement>'
        }
      },
      buttonRef: {
        defaultValue: null,
        description: '',
        name: 'buttonRef',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLButtonElement> | Ref<HTMLAnchorElement>'
        }
      }
    },
    extendedInterfaces: ['AnchorHTMLAttributes', 'OuiButtonIconProps', 'CommonProps', 'ButtonHTMLAttributes', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}
try {
  OuiSmallButtonIcon.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src/components/button/button_icon/button_icon.tsx',
    description: '',
    displayName: 'OuiSmallButtonIcon',
    methods: [],
    props: {
      href: {
        defaultValue: null,
        description: '',
        name: 'href',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AnchorHTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      color: {
        defaultValue: {
          value: 'primary'
        },
        description: 'Any of the named color palette options.\n' + '**`subdued` set to be DEPRECATED, use `text` instead**',
        name: 'color',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"text" | "subdued" | "success" | "accent" | "danger" | "warning" | "ghost" | "primary"',
          value: [{
            value: '"text"'
          }, {
            value: '"subdued"'
          }, {
            value: '"success"'
          }, {
            value: '"accent"'
          }, {
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"ghost"'
          }, {
            value: '"primary"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-labelledby': {
        defaultValue: null,
        description: 'Identifies the element (or elements) that labels the current element.\n' + '@see aria-describedby.',
        name: 'aria-labelledby',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLAnchorElement> | MouseEventHandler<HTMLButtonElement>'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      iconType: {
        defaultValue: null,
        description: '',
        name: 'iconType',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: true,
        type: {
          name: 'IconType'
        }
      },
      type: {
        defaultValue: {
          value: 'button'
        },
        description: '',
        name: 'type',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'ButtonHTMLAttributes'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AnchorHTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: '',
        name: 'isDisabled',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isSelected: {
        defaultValue: null,
        description: 'Applies the boolean state as the `aria-pressed` property to create a toggle button.\n' + '*Only use when the readable text does not change between states.*',
        name: 'isSelected',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      buttonRef: {
        defaultValue: null,
        description: '',
        name: 'buttonRef',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLButtonElement> | Ref<HTMLAnchorElement>'
        }
      },
      iconSize: {
        defaultValue: {
          value: 'm'
        },
        description: 'Size of the icon only.\n' + 'This will not affect the overall size of the button',
        name: 'iconSize',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l" | "xl" | "xxl" | "original"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }, {
            value: '"xxl"'
          }, {
            value: '"original"'
          }]
        }
      },
      display: {
        defaultValue: {
          value: 'empty'
        },
        description: 'Sets the display style for matching other OuiButton types.\n' + '`base` is equivelant to a typical OuiButton\n' + '`fill` is equivelant to a filled OuiButton\n' + '`empty` (default) is equivelant to an OuiButtonEmpty',
        name: 'display',
        parent: {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }, {
          fileName: 'src/src/components/button/button_icon/button_icon.tsx',
          name: 'OuiButtonIconProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"base" | "empty" | "fill"',
          value: [{
            value: '"base"'
          }, {
            value: '"empty"'
          }, {
            value: '"fill"'
          }]
        }
      }
    },
    extendedInterfaces: ['AnchorHTMLAttributes', 'OuiButtonIconProps', 'CommonProps', 'ButtonHTMLAttributes', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}